// Curated release notes — FUNCTIONAL changes only (new capabilities / behaviour), never bug-fix
// details. Newest first. Bilingual so the page follows the UI language. Update this when a
// user-facing feature ships.
//
// These versions are written by hand and mark FEATURE releases, so they move by minor. The number
// in the footer is a different thing: the build's own version (lib/version.ts), whose patch the
// deploy bumps automatically. A footer showing v0.6.4 against a newest note of 0.6.0 is normal —
// four deploys have shipped since that feature set.

export interface Bi {
  en: string
  fr: string
}
export interface Release {
  version: string
  /** ISO date (YYYY-MM-DD). */
  date: string
  title: Bi
  highlights: Bi[]
}

export const RELEASES: Release[] = [
  {
    version: '0.7.0',
    date: '2026-08-14',
    title: { en: 'Deployed live the first MVP scope as Niobe web application and Niobe desktop.', fr: 'Mise en production du périmètre du premier MVP : application web Niobe et application de bureau Niobe.' },
    highlights: [
      {
        en: 'Rearchitecture to isolate a Niobe web lightweight (MVP1) and a Desktop application (MVP2) packaged with Electron',
        fr: "Réarchitecture pour distinguer une application web Niobe allégée (MVP1) et une application de bureau (MVP2) packagée avec Electron",
      },
      {
        en: 'Expose 2 MCP: niobe-edit and niobe-graph for advanced users in MVP1 conditions',
        fr: 'Exposition de 2 serveurs MCP : niobe-edit et niobe-graph, pour les utilisateurs avancés dans les conditions du MVP1',
      },
      {
        en: 'You can now attach the bid site (sharepoint), browse it, and selection/deselection items for indexing as bid knowledge base',
        fr: "Vous pouvez désormais rattacher le site SharePoint de l'appel d'offres, le parcourir, et sélectionner/désélectionner des éléments à indexer comme base de connaissances de l'appel d'offres",
      },
      {
        en: 'Added bid member management and related access management',
        fr: "Ajout de la gestion des membres de l'appel d'offres et des droits d'accès associés",
      },
      {
        en: 'Sign in with your Capgemini Entra ID account — real single sign-on, no more dev/stub authentication',
        fr: 'Connexion avec votre compte Capgemini Entra ID — authentification unique réelle, fini l’authentification de développement',
      },
      {
        en: 'Stop an AI response mid-stream from the chat; the desktop app now shows an in-app "update ready — Restart" banner when a new version is available',
        fr: 'Interrompre une réponse de l’IA en cours de génération depuis le chat ; l’application de bureau affiche désormais un bandeau « mise à jour prête — Redémarrer » lorsqu’une nouvelle version est disponible',
      },
      {
        en: 'Choose your colour scheme and text size, remembered per browser; the whole UI now runs on a generated design-token system mirrored in Figma',
        fr: 'Choisir son thème de couleurs et la taille du texte, mémorisés par navigateur ; toute l’IHM repose désormais sur un système de tokens de design généré et reflété dans Figma',
      },
    ],
  },
    {
    version: '0.6.0',
    date: '2026-06-26',
    title: { en: 'Solar integration — bid workflow, exports & chat skills', fr: 'Intégration Solar — workflow d’appel d’offres, exports & skills du chat' },
    highlights: [
      {
        en: 'Track a bid as a workflow: a dashboard (DAG or table view) shows each section’s status (to do / in progress / to review / validated / blocked), its dependencies and the critical path, plus the bid’s overall status — initialize a workflow per bid.',
        fr: 'Piloter un appel d’offres comme un workflow : un tableau de bord (vue DAG ou tableau) montre le statut de chaque section (à faire / en cours / à revoir / validé / bloqué), ses dépendances et le chemin critique, ainsi que le statut global — un workflow s’initialise par appel d’offres.',
      },
      {
        en: 'Export a document to Excel (XLSX) and Word (DOCX).',
        fr: 'Exporter un document en Excel (XLSX) et en Word (DOCX).',
      },
      {
        en: 'Run skills straight from the chat with slash-commands — generate/draft a section, export to Excel/Word, and run an AI coherence review.',
        fr: 'Lancer des skills directement depuis le chat via des commandes slash — générer/rédiger une section, exporter en Excel/Word, et lancer une relecture de cohérence par l’IA.',
      },
      {
        en: 'AI coherence review: check consistency across the document’s sections.',
        fr: 'Relecture de cohérence par l’IA : vérifier la cohérence entre les sections du document.',
      },
    ],
  },
  {
    version: '0.5.0',
    date: '2026-06-25',
    title: { en: 'Restore, bring-your-own-AI & reading comfort', fr: 'Restauration, IA externe & confort de lecture' },
    highlights: [
      {
        en: 'Restore a document to any point in its modification history (with a divergence-safe guard).',
        fr: "Restaurer un document à n'importe quel point de son historique de modifications (avec garde anti-divergence).",
      },
      {
        en: 'Edit a document with your OWN AI via the passerelle bridge — the UI generates a ready-to-use skill.',
        fr: "Modifier un document avec VOTRE IA via la passerelle — l'IHM génère un skill prêt à l'emploi.",
      },
      {
        en: 'Zoom the document content only (50–200%), remembered per browser.',
        fr: 'Zoomer uniquement le contenu du document (50–200 %), mémorisé par navigateur.',
      },
    ],
  },
  {
    version: '0.4.0',
    date: '2026-06-24',
    title: { en: 'A real chat, model choice & shareable bids', fr: 'Un vrai chat, choix du modèle & liens par appel d’offres' },
    highlights: [
      {
        en: 'The AI chat now remembers the conversation (chat text only), with a context-size meter and a Clear button, capped at 20k tokens.',
        fr: "Le chat IA garde désormais la conversation (texte du chat uniquement), avec une jauge de contexte et un bouton Vider, plafonné à 20k tokens.",
      },
      {
        en: 'Choose the LLM per agent — Claude tiers or the live GitHub Copilot catalogue (Claude & GPT).',
        fr: 'Choisir le LLM par agent — niveaux Claude ou le catalogue GitHub Copilot en direct (Claude & GPT).',
      },
      {
        en: 'A dedicated, shareable URL for each bid (with browser back/forward and deep links).',
        fr: 'Une URL dédiée et partageable par appel d’offres (avec précédent/suivant et liens profonds).',
      },
      {
        en: 'Any error is now surfaced in a clear centered popup.',
        fr: "Toute erreur est désormais signalée dans une popup centrée claire.",
      },
    ],
  },
  {
    version: '0.3.0',
    date: '2026-06-20',
    title: { en: 'Home page & agentic editing', fr: 'Page d’accueil & édition agentique' },
    highlights: [
      {
        en: 'A home page to see your bids, open them, and create a new bid; return to a bid’s overview anytime.',
        fr: 'Une page d’accueil pour voir vos appels d’offres, les ouvrir et en créer ; revenir à la vue d’ensemble d’un appel d’offres à tout moment.',
      },
      {
        en: 'Asynchronous, refresh-proof agentic chat that reports the message, tokens used and processing time.',
        fr: 'Chat agentique asynchrone et résistant au rafraîchissement, qui affiche le message, les tokens et le temps de traitement.',
      },
      {
        en: 'The local agent can read, create, rename and edit document parts on your behalf.',
        fr: "L'agent local peut lire, créer, renommer et modifier les parties du document pour vous.",
      },
    ],
  },
  {
    version: '0.2.0',
    date: '2026-06-10',
    title: { en: 'Authoring & collaboration', fr: 'Rédaction & collaboration' },
    highlights: [
      {
        en: 'Create, rename and organise documents and items directly from the UI.',
        fr: "Créer, renommer et organiser documents et éléments directement depuis l'IHM.",
      },
      {
        en: 'Improve a selection with AI; AI edits apply directly and are logged in the modification history.',
        fr: "Améliorer une sélection avec l'IA ; les éditions IA s'appliquent directement et sont journalisées dans l'historique.",
      },
      {
        en: 'See connected users with named live cursors and per-document presence counts.',
        fr: 'Voir les utilisateurs connectés avec des curseurs nommés en direct et le nombre par document.',
      },
    ],
  },
  {
    version: '0.1.0',
    date: '2026-05-30',
    title: { en: 'Foundations', fr: 'Fondations' },
    highlights: [
      {
        en: 'Real-time co-editing (CRDT) in a single rich editor.',
        fr: 'Co-édition temps réel (CRDT) dans un éditeur riche unique.',
      },
      {
        en: 'Security-trimmed knowledge retrieval (GraphRAG + pgvector) grounding the AI answers.',
        fr: "Recherche de connaissances filtrée par sécurité (GraphRAG + pgvector) pour fonder les réponses de l'IA.",
      },
      {
        en: 'A consultable modification history with inline diffs.',
        fr: 'Un historique des modifications consultable avec diffs en ligne.',
      },
    ],
  },
]
